/**
 * EIP-1193 JSON-RPC provider factory for the 1auth passkey authentication system.
 *
 * Creates a browser-side provider object that implements the EIP-1193 standard so any
 * Ethereum library (ethers, viem, wagmi) can use 1auth passkey signing and cross-chain
 * intent execution without modification. All transaction signing is delegated to the
 * passkey service rather than holding private keys in the browser.
 *
 * Supported RPC methods:
 *   - eth_chainId, eth_accounts, eth_requestAccounts
 *   - personal_sign, eth_sign, eth_signTypedData, eth_signTypedData_v4
 *   - eth_sendTransaction
 *   - wallet_connect, wallet_disconnect, wallet_switchEthereumChain
 *   - wallet_sendCalls, wallet_getCallsStatus, wallet_getCallsHistory (EIP-5792)
 *   - wallet_getCapabilities, wallet_getAssets
 *
 * @module
 */

import {
  hexToString,
  isHex,
  numberToHex,
  type Address,
  type Hex,
} from "viem";
import { OneAuthClient, registerSessionForgetHandler } from "./client";
import { getSupportedChainIds } from "./registry";
import type { CloseOnStatus, IntentCall, IntentTokenRequest, SignerType } from "./types";
import { toOneAuthError } from "./errors";
import { encodeWebAuthnSignature } from "./walletClient/utils";

type ProviderRequest = {
  method: string;
  params?: unknown[] | Record<string, unknown>;
};

type Listener = (...args: unknown[]) => void;

type StoredUser = {
  address: Address;
  /** Absent = legacy passkey user; populated on new sessions. */
  signerType?: SignerType;
  /** Wagmi connector id for EOA sessions. Iframe-side lookup uses this. */
  connectorId?: string;
};

export type OneAuthProvider = {
  request: (args: ProviderRequest) => Promise<unknown>;
  on: (event: string, listener: Listener) => void;
  removeListener: (event: string, listener: Listener) => void;
  disconnect: () => Promise<void>;
  /**
   * Bind the active session in-memory and persist it to `localStorage`.
   *
   * Used by {@link createOneAuthConnection} to hand a freshly connected session
   * to the provider the instant `connect()` resolves — before the app has
   * written `1auth-user` itself. This closes a race where a provider built right
   * after connect would read stale/empty storage and default an EOA session to
   * passkey intents. Passing `null` clears the session (same effect as
   * `disconnect()` minus the events).
   *
   * Existing `createOneAuthProvider` consumers never call this, so their
   * behavior is unchanged: `getStoredUser` falls back to the localStorage read.
   */
  setSession: (
    session:
      | { address: Address; signerType?: SignerType; connectorId?: string }
      | null,
  ) => void;
};

export type OneAuthProviderOptions = {
  client: OneAuthClient;
  chainId: number;
  storageKey?: string;
  /** When to close the dialog and return success. Defaults to "preconfirmed" */
  closeOn?: CloseOnStatus;
  waitForHash?: boolean;
  hashTimeoutMs?: number;
  hashIntervalMs?: number;
};

const DEFAULT_STORAGE_KEY = "1auth-user";

/**
 * Creates an EIP-1193 compatible JSON-RPC provider backed by 1auth passkey authentication.
 *
 * The returned provider object can be used anywhere a standard Ethereum provider is
 * accepted (e.g. passed to `createWalletClient` in viem, used as `window.ethereum`, or
 * supplied to ethers.js `BrowserProvider`). Connection state (the account address) is
 * persisted in `localStorage` under the configured `storageKey`.
 *
 * @param options - Provider configuration
 * @param options.client - Configured `OneAuthClient` instance used to open auth/sign dialogs
 * @param options.chainId - Default chain ID the provider reports via `eth_chainId`
 * @param options.storageKey - localStorage key for persisting the connected user.
 *   Defaults to `"1auth-user"`. Override to support multiple concurrent connections.
 * @param options.closeOn - Controls when the signing dialog closes and shows the "Done" button.
 *   `"preconfirmed"` (default) closes as soon as the intent is confirmed by the orchestrator.
 *   The `waitForHash` option independently controls whether the SDK continues polling for
 *   a transaction hash after the dialog closes.
 * @param options.waitForHash - When true, polls the intent status until a transaction hash
 *   is available before resolving `eth_sendTransaction` / `wallet_sendCalls`. Defaults to true.
 * @param options.hashTimeoutMs - Maximum milliseconds to wait for a transaction hash.
 *   Passed directly to the passkey service intent submission.
 * @param options.hashIntervalMs - Polling interval in milliseconds when waiting for a hash.
 *   Passed directly to the passkey service intent submission.
 * @returns An EIP-1193 provider with `request`, `on`, `removeListener`, and `disconnect`.
 *
 * @example
 * ```typescript
 * import { OneAuthClient } from "@rhinestone/1auth";
 * import { createOneAuthProvider } from "@rhinestone/1auth";
 *
 * const client = new OneAuthClient({ clientId: "my-app" });
 * const provider = createOneAuthProvider({ client, chainId: 8453 }); // Base
 *
 * // Use with viem
 * import { createWalletClient, custom } from "viem";
 * import { base } from "viem/chains";
 * const walletClient = createWalletClient({ chain: base, transport: custom(provider) });
 *
 * // Standard EIP-1193 usage
 * const accounts = await provider.request({ method: "eth_requestAccounts" });
 * provider.on("accountsChanged", (accounts) => console.log(accounts));
 * ```
 */
export function createOneAuthProvider(
  options: OneAuthProviderOptions
): OneAuthProvider {
  const { client } = options;
  let chainId = options.chainId;
  const storageKey = options.storageKey || DEFAULT_STORAGE_KEY;

  const listeners = new Map<string, Set<Listener>>();

  // In-memory session override. When set (via setSession), it takes precedence
  // over the localStorage read so a just-connected provider immediately knows
  // whether it's driving an EOA (forward to wallet, no intents) or a passkey
  // (intents). `undefined` = not bound → fall back to localStorage.
  //
  // Cleared to `undefined` (never pinned to a "signed out" sentinel) on
  // disconnect/setSession(null): those also wipe localStorage, so falling back
  // to the (now empty) storage yields null anyway — and it lets the built-in
  // connect() path repopulate via setStoredUser without a stale override
  // shadowing it. Pinning it null here would break reconnect for plain
  // createOneAuthProvider consumers.
  let sessionOverride: StoredUser | undefined;

  /**
   * Dispatches an event to all registered listeners for the given event name.
   *
   * @param event - EIP-1193 event name (e.g. "accountsChanged", "chainChanged")
   * @param args - Arguments forwarded to each listener callback
   */
  const emit = (event: string, ...args: unknown[]) => {
    const set = listeners.get(event);
    if (!set) return;
    for (const listener of set) listener(...args);
  };

  /**
   * Reads the connected user from localStorage.
   *
   * Returns null in non-browser environments (SSR) and when no user has been
   * persisted yet, or when the stored value is malformed.
   *
   * @returns The stored user object, or null if unavailable
   */
  const getStoredUser = (): StoredUser | null => {
    // A set in-memory binding wins over storage (so a just-connected session
    // routes correctly before the app persists it). `undefined` = unbound, so
    // read from storage — this is also the state after disconnect, letting the
    // built-in connect() repopulate storage and be seen on the next call.
    if (sessionOverride !== undefined) return sessionOverride;
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredUser;
      if (!parsed?.address) return null;
      return parsed;
    } catch {
      return null;
    }
  };

  /**
   * Persists the connected user to localStorage.
   *
   * No-ops in non-browser (SSR) environments.
   *
   * @param user - User object with at minimum an `address` field
   */
  const setStoredUser = (user: StoredUser) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(storageKey, JSON.stringify(user));
  };

  /**
   * Removes the connected user from localStorage, effectively logging them out
   * from the provider's perspective.
   *
   * No-ops in non-browser (SSR) environments.
   */
  const clearStoredUser = () => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(storageKey);
  };

  /** Clear both persisted and in-memory state after local or dialog logout. */
  const forgetSession = () => {
    sessionOverride = undefined;
    clearStoredUser();
    emit("accountsChanged", []);
    emit("disconnect");
  };

  // Some SSR integrations render with a deferred client and replace it after
  // hydration. Provider construction supported that shape before disconnect
  // fan-out was added, so do not make the bookkeeping WeakMap reject `null`.
  if (client) registerSessionForgetHandler(client, forgetSession);

  /**
   * Connects the user by returning a cached address or opening the auth flow.
   *
   * Returns the dapp-side cached address immediately when present. Otherwise
   * opens the auth modal (`/dialog/auth`) directly — the iframe-side
   * returning-user UX lives inside that flow now. On success the address is
   * stored in localStorage and `accountsChanged` / `connect` events are emitted.
   *
   * @returns Array containing the single connected account address
   * @throws If the user cancels or authentication fails
   */
  const connect = async (): Promise<Address[]> => {
    const stored = getStoredUser();
    if (stored) {
      return [stored.address];
    }

    const authResult = await client.authWithModal();
    if (!authResult.success) {
      throw new Error(authResult.error?.message || "Authentication failed");
    }
    const address = authResult.user?.address;
    const signerType = authResult.signerType;

    if (!address) {
      throw new Error("No account address available");
    }

    setStoredUser({ address, signerType });
    emit("accountsChanged", [address]);
    emit("connect", { chainId: numberToHex(chainId) });
    return [address];
  };

  /**
   * Disconnects the current user by clearing persisted state and emitting events.
   *
   * Emits `accountsChanged` with an empty array and `disconnect` so that consumers
   * (e.g. wagmi) can update their state accordingly.
   */
  const disconnect = async () => {
    forgetSession();
  };

  /**
   * Bind (or clear) the active session in memory and mirror it to localStorage.
   * See {@link OneAuthProvider.setSession}.
   */
  const setSession = (
    session:
      | { address: Address; signerType?: SignerType; connectorId?: string }
      | null,
  ) => {
    if (!session) {
      forgetSession();
      return;
    }
    sessionOverride = {
      address: session.address,
      signerType: session.signerType,
      connectorId: session.connectorId,
    };
    setStoredUser(sessionOverride);
    emit("accountsChanged", [session.address]);
    emit("connect", { chainId: numberToHex(chainId) });
  };

  /**
   * Returns the currently connected user, triggering the connect flow if needed.
   *
   * Used internally by methods that require an authenticated session (e.g. signing,
   * sending transactions) to guarantee a user is available before proceeding.
   *
   * @returns The stored user object with at minimum an `address` field
   * @throws If connecting fails and no address can be resolved
   */
  const ensureUser = async (): Promise<StoredUser> => {
    const stored = getStoredUser();
    if (stored) return stored;
    const [address] = await connect();
    if (!address) {
      throw new Error("Failed to resolve user session");
    }
    const user = getStoredUser();
    return user || { address };
  };

  /**
   * Read an optional wallet_getAssets account address override from RPC params.
   */
  const getAssetsAccountAddress = (
    assetsParams?: unknown[] | Record<string, unknown>,
  ): string | undefined => {
    const raw = Array.isArray(assetsParams) ? assetsParams[0] : assetsParams;
    if (typeof raw === "string" && raw.trim()) return raw.trim();
    if (!raw || typeof raw !== "object") return undefined;

    const request = raw as Record<string, unknown>;
    const candidates = [
      request.accountAddress,
      request.address,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
    return undefined;
  };

  /**
   * Parses a chain ID value that may arrive as a hex string, decimal string, or number.
   *
   * Handles both `"0x1"` (hex) and `"1"` / `1` (decimal) representations that are
   * commonly mixed across different wallet_switchEthereumChain implementations.
   *
   * @param value - The raw chain ID value from RPC params
   * @returns The numeric chain ID, or undefined if the value cannot be parsed
   */
  const parseChainId = (value: unknown): number | undefined => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      if (value.startsWith("0x")) return Number.parseInt(value, 16);
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  };

  /**
   * Normalizes a transaction value to a decimal string expected by the intent service.
   *
   * Handles the various formats callers may provide: `bigint`, `number`, hex strings
   * (e.g. `"0x38d7ea4c68000"`), or plain decimal strings. Returns undefined for null
   * or unsupported types, allowing callers to fall back to `"0"`.
   *
   * @param value - Raw value from an RPC transaction param
   * @returns Decimal string representation, or undefined if the input is not usable
   */
  const normalizeValue = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === "bigint") return value.toString();
    if (typeof value === "number") return Math.trunc(value).toString();
    if (typeof value === "string") {
      if (value.startsWith("0x")) {
        return BigInt(value).toString();
      }
      return value;
    }
    return undefined;
  };

  /**
   * Converts raw EIP-1193 call objects into the strongly-typed `IntentCall` format
   * expected by the passkey service.
   *
   * Fills in missing `data` with `"0x"` and missing `value` with `"0"` so the
   * intent service never receives undefined fields.
   *
   * @param calls - Raw call objects from `wallet_sendCalls` or `eth_sendTransaction` params
   * @returns Array of normalized `IntentCall` objects
   */
  const normalizeCalls = (calls: unknown[]): IntentCall[] => {
    return calls.map((call) => {
      const c = call as Record<string, unknown>;
      return {
        to: c.to as Address,
        data: (c.data as Hex | undefined) || "0x",
        value: normalizeValue(c.value) || "0",
        label: c.label as string | undefined,
        sublabel: c.sublabel as string | undefined,
        icon: c.icon as string | undefined,
        abi: c.abi as readonly unknown[] | undefined,
      };
    });
  };

  /**
   * Converts raw token request objects into the typed `IntentTokenRequest` format.
   *
   * Token requests allow callers to specify which tokens and amounts should be sourced
   * when funding a cross-chain intent (e.g. "bridge 10 USDC from Optimism to Base").
   * Accepts bigint or string/numeric amounts and normalizes them to bigint.
   *
   * @param requests - Raw token request array from RPC params, or a non-array value
   * @returns Array of typed token requests, or undefined if input is not an array
   */
  const normalizeTokenRequests = (
    requests: unknown
  ): IntentTokenRequest[] | undefined => {
    if (!Array.isArray(requests)) return undefined;
    return requests.map((r) => {
      const req = r as Record<string, unknown>;
      return {
        token: req.token as string,
        amount:
          typeof req.amount === "bigint"
            ? req.amount
            : BigInt(String(req.amount || "0")),
      };
    });
  };

  /**
   * Decodes a message that may be hex-encoded into a human-readable string.
   *
   * Some signers (e.g. MetaMask) encode plain-text messages as hex before passing them
   * to `personal_sign`. This helper reverses that encoding so users see the original
   * text in the signing dialog rather than a raw hex string.
   *
   * @param value - A plain string or hex-encoded string (`"0x..."`)
   * @returns The decoded UTF-8 string, or the original value if decoding fails
   */
  const decodeMessage = (value: string) => {
    if (!isHex(value)) return value;
    try {
      return hexToString(value as Hex);
    } catch {
      return value;
    }
  };

  /**
   * Forwards a raw EIP-1193 request to the iframe at `/dialog/sign-eoa`, which
   * delegates to the active wagmi connector. The wallet's own prompt is the
   * review screen — no in-dialog review step for EOA sessions.
   */
  const forwardToWallet = async (
    method: string,
    params?: unknown[] | Record<string, unknown>,
    expectedAddress?: Address,
  ): Promise<unknown> => {
    const result = await client.requestWithWallet({
      method,
      params,
      expectedAddress,
    });
    if (!result.success) {
      const err = new Error(result.error.message);
      (err as Error & { code?: string }).code = result.error.code;
      throw err;
    }
    return result.result;
  };

  /**
   * EIP-1193 methods forwarded verbatim to the EOA connector. All other methods
   * (eth_accounts, eth_chainId, wallet_connect, wallet_disconnect,
   * wallet_switchEthereumChain, wallet_getCapabilities/Assets) are handled
   * locally against the stored session even for EOA sessions.
   */
  const EOA_FORWARDED_METHODS = new Set([
    "personal_sign",
    "eth_sign",
    "eth_signTypedData",
    "eth_signTypedData_v3",
    "eth_signTypedData_v4",
    "eth_sendTransaction",
    "wallet_sendCalls",
    "wallet_getCallsStatus",
  ]);

  /**
   * Signs an arbitrary string message via the passkey service.
   *
   * Ensures a user session exists before delegating to `OneAuthClient.signMessage`.
   * The raw WebAuthn signature components are ABI-encoded for ERC-1271 on-chain
   * verification before being returned.
   *
   * @param message - Plain-text message to sign
   * @returns ABI-encoded WebAuthn signature as a hex string
   * @throws If no user session is available or the signing dialog is cancelled
   */
  const signMessage = async (message: string) => {
    const user = await ensureUser();
    if (!user.address) {
      throw new Error("Account address required for signing.");
    }
    const result = await client.signMessage({
      accountAddress: user.address,
      message,
    });
    if (!result.success) {
      throw toOneAuthError(result.error, "Signing failed", "SIGNING_FAILED");
    }
    if (!result.signature) {
      throw toOneAuthError(undefined, "No signature received", "SIGNING_FAILED");
    }
    return encodeWebAuthnSignature(result.signature);
  };

  /**
   * Signs EIP-712 typed data via the passkey service.
   *
   * Accepts either a parsed typed-data object or a JSON string (as some libraries
   * serialize the payload before passing it through the provider). The raw WebAuthn
   * signature is ABI-encoded for ERC-1271 on-chain verification.
   *
   * @param typedData - EIP-712 typed data object or its JSON string representation
   * @returns ABI-encoded WebAuthn signature as a hex string
   * @throws If no user session is available or the signing dialog is cancelled
   */
  const signTypedData = async (typedData: unknown) => {
    const user = await ensureUser();
    if (!user.address) {
      throw new Error("Account address required for signing.");
    }
    const data =
      typeof typedData === "string" ? JSON.parse(typedData) : typedData;
    const result = await client.signTypedData({
      accountAddress: user.address,
      domain: (data as any).domain,
      types: (data as any).types,
      primaryType: (data as any).primaryType,
      message: (data as any).message,
    });
    if (!result.success) {
      throw toOneAuthError(result.error, "Signing failed", "SIGNING_FAILED");
    }
    if (!result.signature) {
      throw toOneAuthError(undefined, "No signature received", "SIGNING_FAILED");
    }
    return encodeWebAuthnSignature(result.signature);
  };

  /**
   * Extracts the fields required by `OneAuthClient.sendIntent` from a richer payload.
   *
   * This thin wrapper makes it explicit which fields travel to the intent service and
   * which are local concerns (e.g. `sourceChainId` is handled at the call site).
   *
   * @param payload - Full intent payload including user identity and transaction data
   * @returns Subset of fields forwarded to the intent service
   */
  const resolveIntentPayload = (payload: {
    accountAddress: Address;
    targetChain: number;
    calls: IntentCall[];
    tokenRequests?: IntentTokenRequest[];
  }) => ({
    accountAddress: payload.accountAddress,
    targetChain: payload.targetChain,
    calls: payload.calls,
    tokenRequests: payload.tokenRequests,
  });

  /**
   * Submits a cross-chain intent through the passkey service and returns the intent ID.
   *
   * Opens the signing dialog, waits for user confirmation, then polls the orchestrator
   * until a transaction hash is available (controlled by `waitForHash` and the timeout
   * options). The returned intent ID is used as the `callsId` in EIP-5792 responses.
   *
   * The dialog closes based on `closeOn` (defaults to `"preconfirmed"`). The `waitForHash`
   * option independently polls for a transaction hash after the dialog closes.
   *
   * @param payload - Intent details including target chain, calls, token requests, and
   *   an optional `sourceChainId` for cross-chain bridging
   * @returns The orchestrator intent ID string
   * @throws If the user rejects the signing dialog or the intent fails
   */
  const sendIntent = async (payload: {
    accountAddress: Address;
    targetChain: number;
    calls: IntentCall[];
    tokenRequests?: IntentTokenRequest[];
    sourceChainId?: number;
  }) => {
    const intentPayload = resolveIntentPayload(payload);
    const result = await client.sendIntent({
      ...intentPayload,
      tokenRequests: payload.tokenRequests,
      sourceChainId: payload.sourceChainId,
      closeOn: options.closeOn,
      waitForHash: options.waitForHash ?? true,
      hashTimeoutMs: options.hashTimeoutMs,
      hashIntervalMs: options.hashIntervalMs,
    });

    if (!result.success) {
      throw toOneAuthError(result.error, "Transaction failed", "INTENT_FAILED");
    }

    // Return intentId as callsId for EIP-5792 compatibility
    return result.intentId;
  };

  const accountMismatchError = (): Error & { code?: string } => {
    const err = new Error(
      "Requested signer does not match the active wallet connection.",
    ) as Error & { code?: string };
    err.code = "ACCOUNT_MISMATCH";
    return err;
  };

  const sameAddress = (a: unknown, b: string): boolean =>
    typeof a === "string" && a.toLowerCase() === b.toLowerCase();

  /**
   * Some wallets (MetaMask, Rabby) reject `eth_sendTransaction` /
   * `wallet_sendCalls` / `personal_sign` / `eth_signTypedData_v4` when the
   * signer address is missing. The dapp doesn't know the EOA address, so the
   * SDK fills it in from the stored session.
   *
   * If the caller supplies a `from` (or address arg) that differs from the
   * connected EOA, we throw `ACCOUNT_MISMATCH` rather than letting the
   * caller's value win. A dapp asking the wallet to sign on behalf of a
   * different account than the user connected is a trust-boundary violation
   * — the user reviewed and approved exactly one address, not whatever the
   * dapp later substitutes.
   */
  const injectEoaSigner = (
    method: string,
    params: unknown[] | Record<string, unknown> | undefined,
    address: Address,
  ): unknown[] | Record<string, unknown> | undefined => {
    const list = Array.isArray(params) ? params.slice() : params;
    if (!Array.isArray(list)) return params;

    if (method === "eth_sendTransaction") {
      const first = (list[0] || {}) as Record<string, unknown>;
      if (first.from !== undefined && !sameAddress(first.from, address)) {
        throw accountMismatchError();
      }
      list[0] = { ...first, from: address };
      return list;
    }
    if (method === "wallet_sendCalls") {
      const first = (list[0] || {}) as Record<string, unknown>;
      if (first.from !== undefined && !sameAddress(first.from, address)) {
        throw accountMismatchError();
      }
      // EIP-5792 lets each call carry its own `from`. Validate every one —
      // a single mismatched call would otherwise sneak through unchecked.
      const calls = first.calls;
      if (Array.isArray(calls)) {
        for (const call of calls) {
          if (!call || typeof call !== "object") continue;
          const callFrom = (call as Record<string, unknown>).from;
          if (callFrom !== undefined && !sameAddress(callFrom, address)) {
            throw accountMismatchError();
          }
        }
      }
      list[0] = { ...first, from: address };
      return list;
    }
    if (method === "personal_sign") {
      // personal_sign(message, address) — fill address if absent.
      if (list[1] !== undefined && !sameAddress(list[1], address)) {
        throw accountMismatchError();
      }
      if (list[0] && list[1] === undefined) list[1] = address;
      return list;
    }
    if (
      method === "eth_sign" ||
      method === "eth_signTypedData" ||
      method === "eth_signTypedData_v3" ||
      method === "eth_signTypedData_v4"
    ) {
      // signTypedData(address, typedData) — fill address if absent.
      if (list[0] !== undefined && !sameAddress(list[0], address)) {
        throw accountMismatchError();
      }
      if (list[0] === undefined && list[1] !== undefined) list[0] = address;
      return list;
    }
    return list;
  };

  // EIP-5792 batches submitted on EOA sessions, keyed by the returned id so
  // wallet_getCallsStatus can answer without hitting the wallet (most EOA
  // wallets don't implement 5792). Lost on reload — getCallsStatus tolerates a
  // miss. ponytail: in-memory is fine; the id IS the last tx hash, so a caller
  // that lost the map can still fetch the receipt itself.
  const eoaBatches = new Map<string, string[]>();

  /**
   * EOA `wallet_sendCalls`: most EOA wallets don't implement EIP-5792, and an
   * EOA can't batch atomically anyway, so send each call as a sequential
   * `eth_sendTransaction` (the standard wagmi/RainbowKit fallback). Returns the
   * last tx hash as the batch id — matching the string-id shape the passkey
   * path returns.
   */
  const sendCallsEoa = async (
    params: unknown[] | Record<string, unknown> | undefined,
    address: Address,
  ): Promise<string> => {
    // Reuse injectEoaSigner for the account-mismatch validation, then decompose.
    const validated = injectEoaSigner("wallet_sendCalls", params, address);
    const payload = (Array.isArray(validated) ? validated[0] : {}) as Record<string, unknown>;
    const calls = Array.isArray(payload.calls) ? payload.calls : [];
    if (!calls.length) throw new Error("No calls provided");
    const batchChainId = payload.chainId;
    const hashes: string[] = [];
    for (const raw of calls) {
      const call = (raw || {}) as Record<string, unknown>;
      const tx: Record<string, unknown> = { from: address, to: call.to };
      if (call.data !== undefined) tx.data = call.data;
      if (call.value !== undefined) tx.value = call.value;
      const callChain = call.chainId ?? batchChainId;
      if (callChain !== undefined) tx.chainId = callChain;
      const hash = await forwardToWallet("eth_sendTransaction", [tx], address);
      hashes.push(String(hash));
    }
    const id = hashes[hashes.length - 1];
    eoaBatches.set(id, hashes);
    return id;
  };

  /** EOA `wallet_getCallsStatus`: answer from the submitted hashes. */
  const getCallsStatusEoa = (callsId: unknown) => {
    if (!callsId || typeof callsId !== "string") {
      throw new Error("callsId is required");
    }
    const hashes = eoaBatches.get(callsId) ?? [callsId];
    // CONFIRMED is submission-optimistic — eth_sendTransaction resolves on
    // submission, and we don't re-poll the chain here. Receipts carry the real
    // hashes; a caller wanting finality can watch them. ponytail: no receipt
    // watcher — the EOA's own wallet already showed the confirmation UI.
    return {
      status: "CONFIRMED",
      receipts: hashes.map((transactionHash) => ({ transactionHash })),
    };
  };

  const request = async ({ method, params }: ProviderRequest) => {
    // For sign/tx methods, resolve the user first — this opens the auth modal
    // on the very first call so we know the signerType before deciding
    // between the passkey path and forwarding to the wagmi connector. Local-
    // only methods (eth_accounts, eth_chainId, wallet_switchEthereumChain,
    // etc.) fall through to the switch below and read stored state directly.
    if (EOA_FORWARDED_METHODS.has(method)) {
      const user = await ensureUser();
      if (user.signerType === "eoa") {
        // EIP-5792 isn't universally supported by EOA wallets — handle the
        // batch methods locally instead of forwarding them blindly.
        if (method === "wallet_sendCalls") {
          return sendCallsEoa(params, user.address);
        }
        if (method === "wallet_getCallsStatus") {
          const list = Array.isArray(params) ? params : [];
          return getCallsStatusEoa(list[0]);
        }
        return forwardToWallet(
          method,
          injectEoaSigner(method, params, user.address),
          user.address,
        );
      }
    }

    switch (method) {
      case "eth_chainId":
        return numberToHex(chainId);
      case "eth_accounts": {
        const stored = getStoredUser();
        return stored ? [stored.address] : [];
      }
      case "eth_requestAccounts":
        return connect();
      case "wallet_connect":
        return connect();
      case "wallet_disconnect":
        await disconnect();
        return true;
      case "wallet_switchEthereumChain": {
        const [param] = (params as any[]) || [];
        const next = parseChainId(param?.chainId ?? param);
        if (!next) {
          throw new Error("Invalid chainId");
        }
        const stored = getStoredUser();
        if (stored?.signerType === "eoa") {
          await forwardToWallet(method, params, stored.address);
        }
        chainId = next;
        emit("chainChanged", numberToHex(chainId));
        return null;
      }
      case "personal_sign": {
        const paramList = Array.isArray(params) ? params : [];
        const first = paramList[0];
        const second = paramList[1];
        // personal_sign param order varies: some callers send [message, address],
        // others send [hexEncodedMessage, address]. When the first param is hex and
        // the second is a non-hex string we treat the second as the plain message
        // (MetaMask-style). Otherwise we always decode the first param.
        const message =
          typeof first === "string" && first.startsWith("0x") && second
            ? typeof second === "string" && !second.startsWith("0x")
              ? second
              : decodeMessage(first)
            : typeof first === "string"
              ? decodeMessage(first)
              : typeof second === "string"
                ? decodeMessage(second)
                : "";
        if (!message) throw new Error("Invalid personal_sign payload");
        return signMessage(message);
      }
      case "eth_sign": {
        const paramList = Array.isArray(params) ? params : [];
        const message = typeof paramList[1] === "string" ? paramList[1] : "";
        if (!message) throw new Error("Invalid eth_sign payload");
        return signMessage(decodeMessage(message));
      }
      case "eth_signTypedData":
      case "eth_signTypedData_v4": {
        const paramList = Array.isArray(params) ? params : [];
        const typedData = paramList[1] ?? paramList[0];
        return signTypedData(typedData);
      }
      case "eth_sendTransaction": {
        const paramList = Array.isArray(params) ? params : [];
        const tx = (paramList[0] || {}) as Record<string, unknown>;
        const user = await ensureUser();
        const targetChain = parseChainId(tx.chainId) ?? chainId;
        const calls = normalizeCalls([tx]);
        const tokenRequests = normalizeTokenRequests(tx.tokenRequests);
        const txSourceChainId = parseChainId(tx.sourceChainId);
        return sendIntent({
          accountAddress: user.address,
          targetChain,
          calls,
          tokenRequests,
          sourceChainId: txSourceChainId,
        });
      }
      case "wallet_sendCalls": {
        const paramList = Array.isArray(params) ? params : [];
        const payload = (paramList[0] || {}) as Record<string, unknown>;
        const user = await ensureUser();
        const targetChain = parseChainId(payload.chainId) ?? chainId;
        const calls = normalizeCalls((payload.calls as unknown[]) || []);
        const tokenRequests = normalizeTokenRequests(payload.tokenRequests);
        const sourceChainId = parseChainId(payload.sourceChainId);
        if (!calls.length) throw new Error("No calls provided");
        return sendIntent({
          accountAddress: user.address,
          targetChain,
          calls,
          tokenRequests,
          sourceChainId,
        });
      }
      case "wallet_getCapabilities": {
        const stored = getStoredUser();
        if (stored?.signerType === "eoa") {
          return {};
        }

        const paramList = Array.isArray(params) ? params : [];
        // walletAddress is params[0] - we ignore since all accounts have same capabilities
        const requestedChains = paramList[1] as `0x${string}`[] | undefined;

        const chainIds = getSupportedChainIds();
        const capabilities: Record<`0x${string}`, Record<string, unknown>> = {};

        for (const chainId of chainIds) {
          const hexChainId = `0x${chainId.toString(16)}` as `0x${string}`;

          // Filter if specific chains requested
          if (requestedChains && !requestedChains.includes(hexChainId)) {
            continue;
          }

          // All supported chains advertise atomic batching, gasless transactions via
          // paymasters, and cross-chain auxiliary funds (EIP-5792 capability keys).
          capabilities[hexChainId] = {
            atomic: { status: "supported" },
            paymasterService: { supported: true },
            auxiliaryFunds: { supported: true },
          };
        }

        return capabilities;
      }
      case "wallet_getAssets": {
        const explicitAccountAddress = getAssetsAccountAddress(params);
        const user = explicitAccountAddress ? null : await ensureUser();
        // The portfolio endpoint resolves identifiers via resolveUserWhere,
        // and the current SDK account model is address-first. Explicit params
        // let app-level verified sessions query balances without relying on
        // the provider's localStorage cache, while connected EIP-1193 consumers
        // still work with no params.
        const accountAddress = explicitAccountAddress || user?.address;
        if (!accountAddress) {
          throw new Error("wallet_getAssets requires accountAddress or a connected account");
        }
        return client.getAssets({ accountAddress: accountAddress as Address });
      }
      case "wallet_getCallsStatus": {
        const paramList = Array.isArray(params) ? params : [];
        const callsId = paramList[0] as string;
        if (!callsId) {
          throw new Error("callsId is required");
        }
        const statusClientId = client.getClientId();
        const response = await fetch(
          `${client.getProviderUrl()}/api/intent/status/${encodeURIComponent(callsId)}`,
          {
            headers: statusClientId ? { "x-client-id": statusClientId } : {},
          }
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || "Failed to get calls status");
        }
        const data = await response.json();
        // Map intent status to EIP-5792 status strings.
        // Both "failed" and "expired" map to "CONFIRMED" because EIP-5792 uses receipts
        // (and a 0x0 status code) to communicate failure — there is no "FAILED" state.
        const statusMap: Record<string, string> = {
          pending: "PENDING",
          preconfirmed: "PENDING",
          completed: "CONFIRMED",
          failed: "CONFIRMED",
          expired: "CONFIRMED",
        };
        return {
          status: statusMap[data.status] || "PENDING",
          receipts: data.transactionHash
            ? [
                {
                  logs: [],
                  // 0x1 = success, 0x0 = reverted/failed — mirrors EVM receipt status
                  status: data.status === "completed" ? "0x1" : "0x0",
                  blockHash: data.blockHash,
                  blockNumber: data.blockNumber,
                  transactionHash: data.transactionHash,
                },
              ]
            : [],
        };
      }
      case "wallet_getCallsHistory": {
        const paramList = Array.isArray(params) ? params : [];
        const options = (paramList[0] || {}) as {
          limit?: number;
          offset?: number;
          status?: string;
          from?: string;
          to?: string;
        };

        const queryParams = new URLSearchParams();
        if (options.limit) queryParams.set("limit", String(options.limit));
        if (options.offset) queryParams.set("offset", String(options.offset));
        if (options.status) queryParams.set("status", options.status);
        if (options.from) queryParams.set("from", options.from);
        if (options.to) queryParams.set("to", options.to);

        const url = `${client.getProviderUrl()}/api/intent/history${
          queryParams.toString() ? `?${queryParams}` : ""
        }`;

        const historyClientId = client.getClientId();
        const response = await fetch(url, {
          headers: historyClientId ? { "x-client-id": historyClientId } : {},
          credentials: "include",
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || "Failed to get calls history");
        }

        const data = await response.json();

        // Map intent status to EIP-5792 format
        const statusMap: Record<string, string> = {
          pending: "PENDING",
          preconfirmed: "PENDING",
          completed: "CONFIRMED",
          failed: "CONFIRMED",
          expired: "CONFIRMED",
        };

        // intentId IS the orchestrator's ID (used as callsId in EIP-5792)
        return {
          calls: data.intents.map(
            (intent: {
              intentId: string;
              status: string;
              transactionHash?: string;
              targetChain: number;
            }) => ({
              callsId: intent.intentId, // intentId is the orchestrator's ID
              status: statusMap[intent.status] || "PENDING",
              receipts: intent.transactionHash
                ? [{ transactionHash: intent.transactionHash }]
                : [],
              chainId: `0x${intent.targetChain.toString(16)}`,
            })
          ),
          total: data.total,
          hasMore: data.hasMore,
        };
      }
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
  };

  return {
    request,
    on(event, listener) {
      const set = listeners.get(event) ?? new Set();
      set.add(listener);
      listeners.set(event, set);
    },
    removeListener(event, listener) {
      const set = listeners.get(event);
      if (!set) return;
      set.delete(listener);
      if (set.size === 0) listeners.delete(event);
    },
    disconnect,
    setSession,
  };
}
